import { useSelector } from 'react-redux';
import {
  useGetAllAvailableBalancesFromExchangesQuery,
  useGetTopBotsQuery,
  useGetUserAvailableCreditsQuery,
} from '../../store/services/api';
import { selectUserId } from '../../store/slices/user';
import React, { useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import { GetTopAssetsByVolumeInfo } from '../../types/Bot';
import { selectDisplayBots } from '../../store/slices/bot';
import { Tabs } from './types';
import SearchCoin from './SearchCoin';
import PredefinedBaskets from './PredefinedBaskets';
import CopyMeBaskets from './CopyMeBaskets';
import { formatNumber } from '../../helpers/NumberFormatter';

const Discover = () => {
  const [activeTab, setActiveTab] = useState(Tabs.SearchCoin);

  const userId = useSelector(selectUserId);
  const { data: balancesData } =
    useGetAllAvailableBalancesFromExchangesQuery(userId);
  const balances = balancesData?.balances;

  const availableStableFunds = useMemo(() => {
    if (!balances) {
      return {};
    }

    return Object.entries(balances).reduce<
      Record<string, Record<string, number>>
    >((acc, [exchange, balancesByQuoteAsset]) => {
      if (balancesByQuoteAsset.USDT) {
        if (!acc[exchange]) acc[exchange] = {};
        acc[exchange].USDT = balancesByQuoteAsset.USDT;
      }
      if (balancesByQuoteAsset.USDC) {
        if (!acc[exchange]) acc[exchange] = {};
        acc[exchange].USDC = balancesByQuoteAsset.USDC;
      }
      return acc;
    }, {});
  }, [balances]);

  return (
    <Layout>
      <Layout.SideMenu>
        <Layout.SideMenuSection>
          <Layout.SideMenuTitle
            title="Actions"
            count={Object.values(Tabs).length}
          />
          {Object.values(Tabs).map((action) => (
            <Layout.SideMenuItem
              key={action}
              id={action}
              label={action}
              onClick={setActiveTab}
              isActive={activeTab === action}
            />
          ))}
        </Layout.SideMenuSection>

        {!!Object.keys(availableStableFunds).length && (
          <Layout.SideMenuSection>
            <Layout.SideMenuTitle
              title="Balances"
              count={Object.keys(availableStableFunds).length}
            />
            {Object.entries(availableStableFunds).map(([exchange, quotes]) => (
              <React.Fragment key={exchange}>
                <Layout.SideMenuSubTitle title={exchange.toUpperCase()} />
                {Object.entries(quotes).map(([quote, value]) => (
                  <Layout.SideMenuItem
                    key={exchange + quote}
                    id={exchange + quote}
                    label={quote}
                    delta={value}
                    metricValue={formatNumber(value, 2)}
                  />
                ))}
              </React.Fragment>
            ))}
          </Layout.SideMenuSection>
        )}
      </Layout.SideMenu>

      <Layout.Content>
        <Content activeTab={activeTab} />
      </Layout.Content>
    </Layout>
  );
};

interface ContentProps {
  activeTab: Tabs;
}

const Content = ({ activeTab }: ContentProps) => {
  const userId = useSelector(selectUserId);

  const {
    data: creditsData,
    isLoading: isCreditsLoading,
    refetch: refetchCredits,
  } = useGetUserAvailableCreditsQuery(userId);
  const {
    data: balancesData,
    isLoading: isBalancesLoading,
    refetch: refetchBalances,
  } = useGetAllAvailableBalancesFromExchangesQuery(userId);
  const balances = balancesData?.balances;
  const credits = creditsData?.credits;

  const refresh = () => {
    refetchCredits();
    refetchBalances();
  };

  return (
    <>
      {activeTab === Tabs.SearchCoin && (
        <SearchCoin
          balances={balances}
          credits={credits}
          isLoading={isCreditsLoading || isBalancesLoading}
          refresh={refresh}
        />
      )}
      {activeTab === Tabs.PredefinedBaskets && (
        <PredefinedBaskets
          credits={credits}
          balances={balances}
          refresh={refresh}
        />
      )}
      {activeTab === Tabs.CopyMeBaskets && (
        <CopyMeBaskets
          credits={credits}
          balances={balances}
          refresh={refresh}
        />
      )}
    </>
  );
};

export default Discover;
